import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { StateService } from '@uirouter/core';
import { cloneDeep, pick } from 'lodash';
import { TemplateApiService } from 'src/app/api/services/template-api.service';
import { ApiUtilsService } from 'src/app/api/services/api-utils.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { PresentationsService } from 'src/app/presentations/services/presentations.service';
import { Blueprint } from 'src/app/presentations/services/blueprint-generator.service';
import { TemplatesService } from 'src/app/presentations/services/templates.service';
import { ClipboardService } from 'src/app/shared/services/clipboard.service';
import { compareIgnoringFields } from 'src/app/shared/helpers/object.helper';
import { environment } from 'src/environments/environment';
import {
  CapAlertCatalogTemplate,
  CapAlertDisplayData,
  CapAlertFilters,
  CapAlertSettingsTabValue,
  CapAlertsForm,
  CapAlertsFormValue,
  CapAlertsPageError,
  CapAlertsPickerChoice,
  CapAlertsPickerMode,
  CapAlertsTab,
  CapAlertsView,
} from '../../types/cap-alerts-page.types';
import { INTEGRATIONS_LIST_STATE } from '../integrations/integrations-company/integrations-company.routing';
import { CapAlertCustomizationService } from './cap-alert-customization.service';
import {
  CAP_ALERT_TEMPLATES,
  findCapAlertTemplate,
} from './cap-alert-templates';
import {
  CapAlertSettings,
  CapAlertsService,
} from '../integrations/component-integrations/cap-alerts/cap-alerts.service';
import { IntegrationsCompanyDialogsService } from '../integrations/integrations-company/integrations-company-dialogs.service';

const CAP_ALERT_CATALOG_FILTER = 'templateContentTypes:=Common Alert Protocol';
const CAP_ALERT_CATALOG_COUNT = 10;

/** The canvas a blueprint without a size is previewed on. */
const DEFAULT_PREVIEW_WIDTH = 1920;
const DEFAULT_PREVIEW_HEIGHT = 1080;

const SETTINGS_TAB_KEYS = [
  'distributeToAll',
  'distribution',
  'distributeToSubcompanies',
  'subcompanyDistribution',
  'subcompanyDistributeToAll',
  'defaultExpiry',
  'textFields',
  'enableAudio',
] as const;

const FILTER_KEYS = [
  'allowedSenders',
  'allowedStatuses',
  'allowedHandlingCodes',
  'allowedCategories',
  'allowedUrgencies',
  'allowedSeverities',
  'allowedCertainties',
  'allowedEventCodes',
] as const;

const RESET_URL_MESSAGE =
  'If you reset your Web Service URL, you will need to provide the new URL to your CAP Alerts provider, otherwise your alerts will stop working.';

const EXPORT_CANCELLED = 'cancelled';

function settingsToFormValue(settings: CapAlertSettings): CapAlertsFormValue {
  return {
    templateProductCode: settings.templateProductCode ?? '',
    presentationId: settings.presentationId ?? '',
    presentationName: settings.presentationName ?? '',
    userName: settings.userName ?? '',
    password: settings.password ?? '',
    settings: cloneDeep(
      pick(settings, SETTINGS_TAB_KEYS),
    ) as CapAlertSettingsTabValue,
    filters: cloneDeep(pick(settings, FILTER_KEYS)) as CapAlertFilters,
  };
}

/** The settings the form would save: the saved snapshot overlaid with the form. */
function formValueToSettings(
  saved: CapAlertSettings,
  value: CapAlertsFormValue,
): CapAlertSettings {
  return cloneDeep({
    ...saved,
    templateProductCode: value.templateProductCode,
    presentationId: value.presentationId,
    presentationName: value.presentationName,
    userName: value.userName,
    password: value.password,
    ...value.settings,
    ...value.filters,
  });
}

/**
 * State of the CAP Alerts page. Provided by the page component, so each visit
 * starts from the company's current settings. `CapAlertsService` stays the
 * persistence layer; this service holds what is on screen.
 */
@Injectable()
export class CapAlertsPageService {
  private readonly capAlertsService = inject(CapAlertsService);
  private readonly featuresService = inject(FeaturesService);
  private readonly userStateService = inject(UserStateService);
  private readonly templatesService = inject(TemplatesService);
  private readonly templateApiService = inject(TemplateApiService);
  private readonly presentationsService = inject(PresentationsService);
  private readonly apiUtilsService = inject(ApiUtilsService);
  private readonly modalService = inject(ModalService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly dialogsService = inject(IntegrationsCompanyDialogsService);
  private readonly stateService = inject(StateService);
  private readonly companyStateService = inject(CompanyStateService);
  private readonly customizationService = inject(CapAlertCustomizationService);

  readonly view = signal<CapAlertsView>('loading');
  readonly pickerMode = signal<CapAlertsPickerMode>('change');
  readonly activeTab = signal<CapAlertsTab>('settings');
  readonly templates = signal<CapAlertCatalogTemplate[]>([]);
  /** Snapshot of the last settings the company saved. */
  readonly saved = signal<CapAlertSettings | null>(null);
  readonly error = signal<CapAlertsPageError | null>(null);
  readonly saving = signal(false);
  readonly loading = signal(false);
  readonly resetting = signal(false);
  readonly alertKey = signal('');
  readonly changeDate = signal<Date | undefined>(undefined);
  readonly changedBy = signal('');

  private readonly featureAvailable = signal<boolean | null>(false);
  private readonly canEdit = signal(false);
  private readonly riseAdmin = signal(false);

  readonly form: CapAlertsForm = new FormGroup({
    templateProductCode: new FormControl('', { nonNullable: true }),
    presentationId: new FormControl('', { nonNullable: true }),
    presentationName: new FormControl('', { nonNullable: true }),
    userName: new FormControl('', { nonNullable: true }),
    password: new FormControl('', { nonNullable: true }),
    settings: new FormControl({} as CapAlertSettingsTabValue, {
      nonNullable: true,
    }),
    filters: new FormControl({} as CapAlertFilters, { nonNullable: true }),
  });

  private readonly currentFormValue = signal<CapAlertsFormValue>(
    this.form.getRawValue(),
  );

  /** The form's current value, including controls disabled by read-only. */
  readonly formValue = this.currentFormValue.asReadonly();

  /** Whether the plan or the user's role forbids editing. */
  readonly readOnly = computed(
    () => !this.featureAvailable() || !this.canEdit(),
  );

  /** Only a Rise Administrator can pick a presentation instead of a template. */
  readonly canChoosePresentation = computed(
    () => !this.readOnly() && this.riseAdmin(),
  );

  /**
   * The Custom Presentation card: for Rise Administrators, and for any company
   * whose saved settings use one. It follows the saved settings, not the form.
   */
  readonly showCustomPresentation = computed(() => {
    const saved = this.saved();
    return (
      this.riseAdmin() ||
      (!!saved?.presentationId && !saved.templateProductCode)
    );
  });

  /** True while anything the user is waiting for is in flight. */
  readonly busy = computed(
    () => this.loading() || this.saving() || this.resetting(),
  );

  /** Pending changes: the form no longer matches the saved settings. */
  readonly isDirty = computed(() => {
    const saved = this.saved();
    if (!saved) {
      return false;
    }
    return !compareIgnoringFields(
      saved,
      formValueToSettings(saved, this.formValue()),
      [],
      true,
    );
  });

  /** Catalog name of the chosen template, or the presentation's name. */
  readonly templateName = computed(() => {
    const value = this.formValue();
    if (value.templateProductCode) {
      return (
        this.templates().find(
          (template) => template.productCode === value.templateProductCode,
        )?.name ?? ''
      );
    }
    return value.presentationName;
  });

  /** The blueprint of the template in the form; null while loading, for a presentation, or if it failed to load. */
  readonly blueprint = signal<Blueprint | null>(null);
  /** What the company contributes to the preview: its address and published brand logo. */
  readonly displayData = signal<CapAlertDisplayData | null>(null);

  /** The placeholder alert text, as the template's own components take it. */
  readonly previewAttributeData = computed(() => {
    const blueprint = this.blueprint();
    return blueprint
      ? this.customizationService.toPreviewAttributeData(blueprint)
      : '';
  });

  readonly previewLabel = computed(() => {
    const name = this.templateName();
    return name ? `${name} preview` : 'Alert preview';
  });

  /** The canvas width the preview renders at; 0 until a blueprint has loaded, so the placeholder shows. */
  readonly previewContentWidth = computed(() => {
    const blueprint = this.blueprint();
    return blueprint
      ? parseInt(String(blueprint.width), 10) || DEFAULT_PREVIEW_WIDTH
      : 0;
  });

  /** A blueprint without a size is normal: the template editor's preview also falls back to 1920x1080. */
  private readonly previewContentHeight = computed(() => {
    const blueprint = this.blueprint();
    return blueprint
      ? parseInt(String(blueprint.height), 10) || DEFAULT_PREVIEW_HEIGHT
      : DEFAULT_PREVIEW_HEIGHT;
  });

  readonly previewAspectRatio = computed(
    () =>
      (this.previewContentWidth() || DEFAULT_PREVIEW_WIDTH) /
      this.previewContentHeight(),
  );

  /** Set while the alert plays a presentation; a presentation cannot be previewed. */
  readonly previewPresentationName = computed(() => {
    const value = this.formValue();
    if (value.templateProductCode || !value.presentationId) {
      return '';
    }
    return (
      value.presentationName || CAP_ALERT_TEMPLATES.customPresentation.title
    );
  });

  private blueprintRequest = 0;

  readonly alertUrl = computed(
    () => `${environment.ALERTS_WS_URL}?id=${this.alertKey()}`,
  );

  constructor() {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.currentFormValue.set(this.form.getRawValue()));

    // Only a different template reloads the blueprint, not every edit of the form.
    const chosenProductCode = computed(
      () => this.formValue().templateProductCode,
    );
    effect(() => {
      const productCode = chosenProductCode();
      untracked(() => void this.loadBlueprint(productCode));
    });
  }

  async init(): Promise<void> {
    this.view.set('loading');
    this.loading.set(true);
    this.error.set(null);

    try {
      this.capAlertsService.loadSettings();
      this.featureAvailable.set(
        this.featuresService.isFeatureAvailable(FeaturesService.FEATURE_ALERTS),
      );
      this.canEdit.set(this.userStateService.hasPermission('ua'));
      this.riseAdmin.set(this.userStateService.isRiseAdmin());
      this.displayData.set(
        this.customizationService.toDisplayData(
          this.companyStateService.getCopyOfSelectedCompany(true),
        ),
      );
      this.syncFromService();

      const settings = this.capAlertsService.alertSettings;
      if (!settings?.enabled) {
        if (!this.featureAvailable()) {
          await this.sendToUpgrade();
          return;
        }
        this.view.set('disclaimer');
        return;
      }

      if (!(await this.loadCatalog())) {
        return;
      }

      await this.resolvePresentationName(settings);
      this.applySaved(settings);
      this.selectDefaultTemplate();
      this.activeTab.set('settings');
      this.view.set('page');
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Repeats what failed: a catalog failure while accepting the disclaimer
   * repeats the acceptance, anything else reloads the page.
   */
  retry(): Promise<void> {
    return this.view() === 'disclaimer' ? this.accept() : this.init();
  }

  /**
   * Accepting the disclaimer connects the company. A company with no template
   * and no presentation gets Warning, so a connected company always has one;
   * when the catalog has no Warning template, its first template.
   */
  async accept(): Promise<void> {
    if (this.loading() || this.readOnly()) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    try {
      if (!(await this.loadCatalog())) {
        return;
      }

      const defaultCode = (
        findCapAlertTemplate(this.templates(), 'warning') ?? this.templates()[0]
      )?.productCode;

      try {
        await this.capAlertsService.enable(defaultCode);
      } catch {
        // The persistence layer recorded the failure; it is reported below.
      }
      if (this.capAlertsService.errorSaving) {
        this.error.set({
          kind: 'accept',
          title: this.capAlertsService.errorMessage,
          detail: this.capAlertsService.apiError,
        });
        return;
      }
      if (!this.capAlertsService.alertSettings) {
        // Nothing was saved: enable() does nothing for a company without
        // alert settings, so there is no connection to announce.
        this.error.set({
          kind: 'accept',
          title: 'Failed to connect CAP Alerts.',
          detail: "The company's alert settings could not be loaded.",
        });
        return;
      }

      this.dialogsService.showSuccessSnackbar('Connected', 'CAP Alerts');
      this.syncFromService();
      const settings = this.capAlertsService.alertSettings;
      await this.resolvePresentationName(settings);
      this.applySaved(settings);
      this.pickerMode.set('first-connection');
      this.view.set('picker');
    } finally {
      this.loading.set(false);
    }
  }

  /** "Change template": the picker over the page, with the current choice marked. */
  openPicker(): void {
    if (this.readOnly()) {
      return;
    }
    this.pickerMode.set('change');
    this.view.set('picker');
  }

  /** Back from the picker, with nothing changed. */
  closePicker(): void {
    this.view.set('page');
  }

  async choose(choice: CapAlertsPickerChoice): Promise<void> {
    if (choice.kind === 'template') {
      this.chooseTemplate(choice.productCode);
    } else {
      await this.choosePresentation();
    }
  }

  /** The chosen template is a pending change until Save. */
  chooseTemplate(productCode: string): void {
    this.form.patchValue({
      templateProductCode: productCode,
      presentationId: '',
      presentationName: '',
    });
    this.showPageAfterChoice();
  }

  /** @returns whether a presentation was chosen; dismissing the chooser keeps the previous choice. */
  async choosePresentation(): Promise<boolean> {
    if (!this.canChoosePresentation()) {
      return false;
    }

    try {
      const [id, name] =
        await this.presentationsService.openPresentationSelector();
      this.form.patchValue({
        templateProductCode: '',
        presentationId: id,
        presentationName: name,
      });
      this.showPageAfterChoice();
      return true;
    } catch {
      return false;
    }
  }

  dismissError(): void {
    this.error.set(null);
  }

  /** @returns whether the settings were saved. */
  async save(): Promise<boolean> {
    const saved = this.saved();
    if (!saved || this.saving()) {
      return false;
    }

    this.saving.set(true);
    this.error.set(null);
    this.capAlertsService.clearMessages();

    try {
      // `CapAlertsService.save` sends the object it holds, so it is updated in place.
      Object.assign(
        this.capAlertsService.alertSettings,
        formValueToSettings(saved, this.formValue()),
      );
      await this.capAlertsService.save();

      if (this.capAlertsService.missingTemplate) {
        this.error.set({
          kind: 'save',
          title: 'Please select an alerts template.',
          detail: '',
        });
        return false;
      }
      if (this.capAlertsService.errorSaving) {
        this.error.set({
          kind: 'save',
          title: this.capAlertsService.errorMessage,
          detail: this.capAlertsService.apiError,
        });
        return false;
      }

      this.syncFromService();
      this.applySaved(this.capAlertsService.alertSettings);
      this.dialogsService.showSuccessSnackbar('Updated', 'CAP Alerts');
      return true;
    } finally {
      this.saving.set(false);
    }
  }

  /** Discards every pending change and stays on the page. */
  cancel(): void {
    const saved = this.saved();
    if (saved) {
      this.form.reset(settingsToFormValue(saved));
      // Back to how the page opened: a company with nothing chosen yet has the
      // default template selected again.
      this.selectDefaultTemplate();
    }
    this.error.set(null);
  }

  copyAddress(): Promise<void> {
    // The clipboard service already tells the user when copying fails.
    return this.clipboardService.copy(this.alertUrl()).catch(() => undefined);
  }

  async resetUrl(): Promise<void> {
    if (this.readOnly()) {
      return;
    }

    try {
      await this.modalService.confirm(
        'Reset Web Service URL?',
        RESET_URL_MESSAGE,
        'I Understand',
        'Cancel',
        'urlResetButton',
      );
    } catch {
      return;
    }

    this.resetting.set(true);
    this.error.set(null);
    try {
      await this.capAlertsService.regenerateAlertKey();
      if (this.capAlertsService.apiError) {
        this.error.set({
          kind: 'reset',
          title: this.capAlertsService.errorMessage,
          detail: this.capAlertsService.apiError,
        });
      }
      this.syncFromService();
    } finally {
      this.resetting.set(false);
    }
  }

  async exportAlerts(): Promise<void> {
    const userEmail = this.userStateService.getUserEmail();

    if (!userEmail) {
      this.modalService.showMessage(
        'Error',
        'Unable to retrieve email address',
      );
      return;
    }

    const confirmMessage =
      'An export file will be prepared and emailed to you at <b>' +
      userEmail +
      '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.';
    const startedMessage =
      'The alert history export has been initiated. You will receive an email once it is ready.';

    try {
      try {
        await this.modalService.confirm(
          'Export alerts?',
          confirmMessage,
          'Export',
          'Cancel',
        );
      } catch (err) {
        if (!err || (err instanceof Error && err.message === '')) {
          throw new Error(EXPORT_CANCELLED);
        }
        throw err;
      }

      await this.capAlertsService.exportAlerts(userEmail);
      this.modalService.showMessage('Export initiated', startedMessage);
    } catch (err) {
      if (err instanceof Error && err.message === EXPORT_CANCELLED) {
        return;
      }

      this.modalService.showMessage('Error', 'Unable to export at this time.');
      console.error(err);
    }
  }

  /**
   * Loads the blueprint of the chosen template. A failure leaves the preview on
   * its placeholder; the page stays usable. A slow answer for a template the
   * user has already left is dropped.
   */
  private async loadBlueprint(productCode: string): Promise<void> {
    const request = ++this.blueprintRequest;
    this.blueprint.set(null);
    if (!productCode) {
      return;
    }

    try {
      const blueprint =
        await this.customizationService.getBlueprint(productCode);
      if (request === this.blueprintRequest) {
        this.blueprint.set(blueprint);
      }
    } catch (error) {
      console.error('Failed to load the template blueprint.', error);
    }
  }

  /** After the first connection the user lands on Connect; after a change, where they were. */
  private showPageAfterChoice(): void {
    if (this.pickerMode() === 'first-connection') {
      this.activeTab.set('connect');
    }
    this.view.set('page');
  }

  /**
   * Without the plan, today's upgrade path; a confirmed upgrade is already
   * navigating to the purchase flow, so only a dismissal goes back to the
   * integrations list.
   */
  private async sendToUpgrade(): Promise<void> {
    const upgrading = await this.featuresService
      .showUpgradePlanModal(FeaturesService.FEATURE_ALERTS)
      .catch(() => undefined);
    if (upgrading) {
      return;
    }
    await this.stateService.go(INTEGRATIONS_LIST_STATE);
  }

  /** Makes `settings` the saved snapshot and resets the form to it. */
  private applySaved(settings: CapAlertSettings): void {
    this.saved.set(cloneDeep(settings));
    this.form.reset(settingsToFormValue(settings));

    if (this.readOnly()) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  /** Mirrors the persistence layer's plain fields into signals. */
  private syncFromService(): void {
    this.alertKey.set(this.capAlertsService.alertKey ?? '');
    this.changeDate.set(this.capAlertsService.changeDate);
    this.changedBy.set(this.capAlertsService.changedBy ?? '');
  }

  private async loadCatalog(): Promise<boolean> {
    const cached = this.templatesService.capAlerts;
    if (cached?.length) {
      this.templates.set(cached);
      return true;
    }

    try {
      const response = await this.templateApiService.listTemplates({
        count: CAP_ALERT_CATALOG_COUNT,
        filter: CAP_ALERT_CATALOG_FILTER,
      });
      if (!Array.isArray(response?.items) || response.items.length === 0) {
        this.error.set({
          kind: 'catalog',
          title: 'Failed to load alert templates.',
          detail: 'No alert templates are available.',
        });
        console.error('No alert templates are available.', response);
        return false;
      }
      this.templatesService.capAlerts = response.items;
      this.templates.set(response.items);
      return true;
    } catch (error: any) {
      this.error.set({
        kind: 'catalog',
        title: 'Failed to load alert templates.',
        detail: this.apiUtilsService.humanReadableError(
          (error && error.result) || error,
        ),
      });
      console.error('Failed to load alert templates.', error);
      return false;
    }
  }

  /**
   * `CapAlertsService` fills the presentation's name in the background, which
   * can land after the snapshot is taken; wait for it here instead.
   */
  private async resolvePresentationName(
    settings: CapAlertSettings,
  ): Promise<void> {
    if (
      !settings.presentationId ||
      settings.templateProductCode ||
      settings.presentationName
    ) {
      return;
    }

    try {
      const presentation =
        await this.presentationsService.getPresentationCached(
          settings.presentationId,
        );
      settings.presentationName = presentation.name;
    } catch {
      // The heading stays blank; the presentation id is still saved.
    }
  }

  /** Like today's dialog: with nothing chosen yet, the first template is selected but not saved. */
  private selectDefaultTemplate(): void {
    const value = this.form.getRawValue();
    const [first] = this.templates();
    if (!value.templateProductCode && !value.presentationId && first) {
      this.form.controls.templateProductCode.setValue(first.productCode);
    }
  }
}
